// the rich text editor.
import { esc, icon } from './core.ts';
import { toast } from './toast.ts';

/** What a page script can do with an editor through its textarea (`textarea._dsEditor`). */
export interface EditorHandle {
  set(html: string | null | undefined): void;
  get(): string;
}
/** A textarea this script turned into an editor. */
export type EditorTextarea = HTMLTextAreaElement & { _dsEditor?: EditorHandle };

// ---------------- T-072: rich text (replaces Summernote): textarea.summernote / textarea[data-ds-editor] ----------------
// The textarea stays the field the form and the page scripts read ($('#description').val()); the editor writes its
// HTML back on every change. Pasted text comes in as plain text (no foreign styles); the server sanitises on display.
// [command, button face, label]; '|' is a separator
const ED_TOOLS: [string, string?, string?][] = [
  ['bold', '<b>B</b>', 'درشت'],
  ['italic', '<i>I</i>', 'کج'],
  ['underline', '<u>U</u>', 'زیرخط'],
  ['|'],
  ['insertUnorderedList', '•', 'فهرست'],
  ['insertOrderedList', '1.', 'فهرست شماره‌دار'],
  ['|'],
  ['justifyRight', 'راست', 'راست‌چین'],
  ['justifyCenter', 'وسط', 'وسط‌چین'],
  ['justifyLeft', 'چپ', 'چپ‌چین'],
  ['|'],
  ['createLink', icon('paperclip'), 'پیوند'],
  ['removeFormat', icon('close'), 'پاک کردن قالب'],
];
export function enhanceEditor(ta: EditorTextarea): void {
  if (ta.dataset.dsReady) return;
  ta.dataset.dsReady = '1';
  const box = document.createElement('div');
  box.className = 'ds-editor';
  box.innerHTML =
    '<div class="ds-editor-bar" role="toolbar" aria-label="قالب‌بندی متن">' +
    ED_TOOLS.map(t =>
      t[0] === '|'
        ? '<span class="sep" aria-hidden="true"></span>'
        : `<button type="button" class="btn btn-icon" data-cmd="${t[0]}" aria-label="${esc(t[2])}" title="${esc(t[2])}">${t[1]!}</button>`,
    ).join('') +
    '</div><div class="ds-editor-area" contenteditable="true" dir="rtl" role="textbox" aria-multiline="true"></div>';
  ta.parentNode!.insertBefore(box, ta);
  ta.hidden = true;
  const area = box.querySelector<HTMLElement>('.ds-editor-area')!;
  area.innerHTML = ta.value;
  if (ta.id) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- labels is null on a hidden or label-less control
    area.setAttribute('aria-labelledby', [...(ta.labels || [])].map(l => l.id || (l.id = ta.id + '-lbl')).join(' '));
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- labels is null on a hidden or label-less control
    [...(ta.labels || [])].forEach(l => l.addEventListener('click', () => area.focus()));
  }
  if (ta.placeholder) area.dataset.placeholder = ta.placeholder;
  const sync = (): void => {
    ta.value = area.innerHTML.trim() === '<br>' ? '' : area.innerHTML;
    ta.dispatchEvent(new Event('input', { bubbles: true }));
    ta.dispatchEvent(new Event('change', { bubbles: true }));
  };
  area.addEventListener('input', sync);
  // the textarea is hidden: a required one would block the post silently ("not focusable"), so the box shows it
  ta.addEventListener('invalid', e => {
    e.preventDefault();
    box.classList.add('invalid');
    area.focus();
    toast(
      'این بخش را پر کنید: ' +
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- labels is null on a hidden or label-less control
        ([...(ta.labels || [])].map(l => l.textContent.replace('*', '').trim()).join(' ') || 'متن'),
      { type: 'danger' },
    );
  });
  area.addEventListener('input', () => box.classList.remove('invalid'));
  area.addEventListener('paste', e => {
    e.preventDefault();
    // execCommand is deprecated but is still the only way to insert text with undo in a contenteditable
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    document.execCommand('insertText', false, e.clipboardData!.getData('text/plain'));
  });
  box.querySelector('.ds-editor-bar')!.addEventListener('mousedown', e => {
    if ((e.target as Element).closest('[data-cmd]')) e.preventDefault();
  });
  box.querySelector('.ds-editor-bar')!.addEventListener('click', e => {
    const b = (e.target as Element).closest<HTMLElement>('[data-cmd]');
    if (!b) return;
    area.focus();
    if (b.dataset.cmd === 'createLink') {
      const url = prompt('نشانی پیوند (https://…)');
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- see the paste handler
      if (url && /^https?:\/\//i.test(url.trim())) document.execCommand('createLink', false, url.trim());
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- see the paste handler
    } else document.execCommand(b.dataset.cmd!, false, null as unknown as string);
    sync();
  });
  ta._dsEditor = {
    set(html) {
      area.innerHTML = html ?? '';
      ta.value = area.innerHTML;
    },
    get: () => ta.value,
  };
}
